import { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import ConfirmModal from "../../../components/Modals/ConfirmModal";
import PdfPreviewModal from "../../../components/Modals/PdfPreviewModal";
import { toast, confirmDialog } from "../../../components/Toast/toast";
import { AuthContext } from "../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../utils/constants/permissions";
import { btn } from "../../../components/Button/buttonStyles";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "-";

const fmtDateTime = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const daysFromNow = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
};

const Row = ({ label, children, value }) => (
  <div className="flex items-start">
    <span className="w-44 text-sm font-medium text-gray-700 shrink-0">{label}:</span>
    <span className="text-sm text-gray-900">{children ?? value ?? "-"}</span>
  </div>
);

/* Reusable section shell */
const Section = ({ title, count, actionBtn, children }) => (
  <div className="bg-white shadow rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
      <span className="text-xl font-bold text-gray-900">
        {title}
      </span>
      {actionBtn}
    </div>
    {count !== undefined && (
      <p className="text-xs text-gray-500 px-5 pt-3">
        Total <span className="font-semibold">{count}</span>{" "}
        {count === 1 ? "item" : "items"}.
      </p>
    )}
    {children}
  </div>
);

/* Simple table wrapper */
const SectionTable = ({ headers, children, emptyMsg = "No results found." }) => (
  <div className="overflow-x-auto px-5 pb-4 pt-2">
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className="border-b border-gray-200">
          {headers.map((h) => (
            <th
              key={h}
              className="text-left font-semibold text-gray-700 py-2 pr-4 whitespace-nowrap"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
    {!children || (Array.isArray(children) && children.length === 0) ? (
      <p className="text-xs text-gray-400 py-3">{emptyMsg}</p>
    ) : null}
  </div>
);

const ActionBtn = ({ onClick, label, color }) => (
  <button
    onClick={onClick}
    className={`text-xs text-white px-3 py-1.5 rounded shadow ${color}`}
  >
    {label}
  </button>
);

const ClientInvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const readOnly = isReadOnly(user, MODULES.CLIENT_INVOICES);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/project-finances/client-invoices/${id}`, {
        withCredentials: true,
      });
      setData(res.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load invoice.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchInvoice();
  }, [id, fetchInvoice]);

  const handleDelete = async () => {
    try {
      await api.delete(`/project-finances/client-invoices/${id}`, {
        withCredentials: true,
      });
      navigate("/client/invoices");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to delete invoice.");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleMarkAsSent = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(
        `/project-finances/client-invoices/${id}/mark-as-sent`,
        {},
        { withCredentials: true }
      );
      setData(res.data.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to mark as sent.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleWriteOff = async () => {
    if (!await confirmDialog("Write off this invoice?")) return;
    setActionLoading(true);
    try {
      const res = await api.post(
        `/project-finances/client-invoices/${id}/write-off`,
        {},
        { withCredentials: true }
      );
      setData(res.data.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to write off invoice.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center mt-16">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  if (error)
    return <div className="text-center mt-10 text-danger-600 font-medium">{error}</div>;
  if (!data)
    return <div className="text-center mt-10 text-gray-500">Invoice not found.</div>;

  /* Derived values */
  const client = data.client;
  const clientName = client
    ? client.type?.toLowerCase() === "company"
      ? client.company_name || "-"
      : client.primary_user
      ? `${client.primary_user.first_name} ${client.primary_user.last_name}`.trim()
      : "-"
    : "-";

  const contactPerson = data.contactPerson
    ? `${data.contactPerson.first_name} ${data.contactPerson.last_name}`.trim()
    : "-";

  const paymentMethodName = (() => {
    const pm = data.paymentMethod;
    if (!pm) return "-";
    if (pm.bank_transfer_detail?.payment_method_name)
      return pm.bank_transfer_detail.payment_method_name;
    return pm.payment_method?.replace(/_/g, " ") || "-";
  })();

  const allReceivables = (data.receivables || [])
    .map((r) => r.flatRateReceivable || r.unitBasedReceivable)
    .filter(Boolean);

  const currencyCode =
    allReceivables[0]?.currency?.currency?.code ||
    data.flatRateReceivable?.currency?.currency?.code ||
    data.unitBasedReceivable?.currency?.currency?.code ||
    "-";

  const internalNote =
    allReceivables[0]?.internal_note ||
    data.flatRateReceivable?.internal_note ||
    data.unitBasedReceivable?.internal_note ||
    "-";

  const legalEntity = client?.legal_entity || "-";

  const coverage = data.coverage_by_payments || "Not covered";
  const coverageColor =
    coverage === "Covered"
      ? "text-gray-900"
      : coverage === "Partially covered"
      ? "text-amber-600"
      : "text-danger-500";

  const days = daysFromNow(data.due_at);

  /* Receivables rows from grouped response */
  const receivableRows = (data.receivables || []).map((r) => {
    const rec = r.flatRateReceivable || r.unitBasedReceivable;
    const isUnit = !!r.unitBasedReceivable;
    return { rowId: r.rowId, rec, isUnit };
  });

  return (
    <div className="space-y-6">
      {/* Top two-card row (unchanged) */}
      <div className="flex gap-6 items-start">
        {/* Left card */}
        <div className="w-[63%] bg-white shadow rounded-lg p-8">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
            <div className="flex items-center gap-3">
              <BackButton />
              <h2 className="text-xl font-bold text-gray-900 whitespace-nowrap">
                Invoice {data.invoice_code}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setShowPdf(true)}
                className={btn("secondary", "sm")}
              >
                Download PDF
              </button>
              <button
                onClick={() => toast.error("Discount & Extra charge not yet implemented")}
                className={btn("secondary", "sm")}
              >
                Discount &amp; Extra charge
              </button>
              {!readOnly && (
                <>
                  <button
                    onClick={() => navigate(`/client/invoice/${id}/edit`)}
                    className={btn("primary", "sm")}
                  >
                    Update
                  </button>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    disabled={!data.can_delete}
                    title={data.delete_blocked_reason || undefined}
                    className={btn("danger", "sm")}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="space-y-4 pl-4">
            <Row label="Client">
              {client?.id ? (
                <Link
                  to={`/clients/${client.id}`}
                  className="text-brand-600 hover:text-brand-800 underline text-sm"
                >
                  {clientName}
                </Link>
              ) : (
                clientName
              )}
            </Row>
            <Row label="Client contact person" value={contactPerson} />
            <Row label="Issued at" value={fmtDateTime(data.issued_at)} />
            <Row label="Payment terms" value={data.payment_terms || "-"} />
            <Row label="Payment method" value={paymentMethodName} />
            <Row label="Currency" value={currencyCode} />
            <Row label="Subtotal" value={data.total > 0 ? fmtNum(data.total) : "-"} />
            <Row label="Extra charge" value="-" />
            <Row label="Discount" value="-" />
            <Row label="Total (before taxes)" value="-" />
            <Row label="Taxes" value="-" />
            <Row label="Total" value="-" />
            <Row label="Legal entity" value={legalEntity} />
            <Row label="Note for client" value="-" />
            <Row label="Internal note" value={internalNote} />
            <Row label="PDF template" value="-" />
            <Row label="Created at" value={fmtDateTime(data.createdAt)} />
          </div>
        </div>

        {/* Right card (Status) */}
        <div className="flex-1 bg-white shadow rounded-lg p-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-xl font-bold text-gray-900">Status</h2>
            {!readOnly && (
              <div className="flex gap-2">
                <button
                  onClick={() => toast.success("Send not yet implemented")}
                  disabled={actionLoading}
                  className={btn("primary", "sm")}
                >
                  Send
                </button>
                <button
                  onClick={handleMarkAsSent}
                  disabled={actionLoading}
                  className={btn("secondary", "sm")}
                >
                  Mark as sent
                </button>
                <button
                  onClick={handleWriteOff}
                  disabled={actionLoading}
                  className={btn("secondary", "sm")}
                >
                  Write off
                </button>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-start">
              <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
                Last sent at:
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-900">
                  {fmtDateTime(data.last_sent_at)}
                </span>
                <button className="text-xs text-brand-600 hover:underline">
                  View history
                </button>
              </div>
            </div>

            <div className="flex items-start">
              <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
                Due at:
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-900">
                  {fmtDateTime(data.due_at)}
                </span>
                {days !== null && (
                  <span
                    className={`text-xs font-medium ${
                      days < 0 ? "text-danger-500" : "text-amber-500"
                    }`}
                  >
                    {days < 0
                      ? `${Math.abs(days)} days overdue`
                      : `in ${days} days`}
                  </span>
                )}
              </div>
            </div>

            <div className="pt-2">
              <p className="text-sm font-bold text-gray-900 mb-4">
                Coverage by payments
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
                    Coverage by payments:
                  </span>
                  <span className={`text-sm font-medium ${coverageColor}`}>
                    {coverage}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
                    Paid amount:
                  </span>
                  <span className="text-sm text-gray-900">
                    {fmtNum(data.paid_amount)}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
                    Due amount:
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    {fmtNum(data.due_amount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Taxes */}
      <Section
        title="Taxes"
        count={0}
        actionBtn={
          <div className="flex gap-2">
            <ActionBtn
              onClick={() => toast.error("Add percentage tax not yet implemented")}
              label="Add percentage tax"
              color="bg-brand-600 hover:bg-brand-700"
            />
            <ActionBtn
              onClick={() => toast.error("Add absolute tax not yet implemented")}
              label="Add absolute tax"
              color="bg-brand-600 hover:bg-brand-700"
            />
          </div>
        }
      >
        <SectionTable headers={["Tax", "%", "Value", ""]}>
          <tr>
            <td colSpan={4} className="py-3 text-xs text-gray-400">
              No results found.
            </td>
          </tr>
        </SectionTable>
      </Section>

      {/* Receivables */}
      <Section
        title="Receivables"
        count={receivableRows.length}
        actionBtn={
          <ActionBtn
            onClick={() => toast.error("Add receivables not yet implemented")}
            label="Add receivables"
            color="bg-brand-600 hover:bg-brand-700"
          />
        }
      >
        <div className="overflow-x-auto px-5 pb-4 pt-2">
          <table className="w-full text-xs border-collapse min-w-[1200px]">
            <thead>
              <tr className="border-b border-gray-200">
                {[
                  "Code", "Project", "PO number", "Service", "Language pair",
                  "Unit amount", "Unit", "Price per unit", "Subtotal",
                  "Extra charge", "Discount", "Total",
                  "Filename", "Note for client", "Internal note", "",
                ].map((h) => (
                  <th key={h} className="text-left font-semibold text-gray-700 py-2 pr-4 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {receivableRows.length === 0 ? (
                <tr>
                  <td colSpan={17} className="py-3 text-xs text-gray-400">No results found.</td>
                </tr>
              ) : (
                receivableRows.map(({ rowId, rec, isUnit }) => {
                  const lp = rec?.languagePair;
                  const langPair = lp
                    ? `${lp.sourceLanguage?.name || lp.sourceLanguage?.code || "?"} → ${lp.targetLanguage?.name || lp.targetLanguage?.code || "?"}`
                    : "—";
                  const project = rec?.project;

                  return (
                    <tr key={rowId} className="border-b border-gray-100 hover:bg-gray-50">
                      {/* Code */}
                      <td className="py-2 pr-4 whitespace-nowrap">
                        <Link
                          to={`/client/receivable/${rec?.id}?type=${isUnit ? "unit_based" : "flat_rate"}`}
                          className="text-teal-600 hover:text-teal-800 underline font-medium"
                        >
                          {rec?.receivable_code || "—"}
                        </Link>
                      </td>
                      {/* Project */}
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {project?.id ? (
                          <Link to={`/project/${project.id}`} className="text-brand-600 hover:underline">
                            P{project.id}
                          </Link>
                        ) : "—"}
                      </td>
                      {/* PO number */}
                      <td className="py-2 pr-4 text-gray-700">{rec?.po_number || "—"}</td>
                      {/* Service */}
                      <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">{rec?.service?.name || "—"}</td>
                      {/* Language pair */}
                      <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">{langPair}</td>
                      {/* Unit amount - unit_based only */}
                      <td className="py-2 pr-4 text-right text-gray-700">
                        {isUnit ? (rec?.unit_amount ?? "—") : <span className="text-gray-300">—</span>}
                      </td>
                      {/* Unit - unit_based only */}
                      <td className="py-2 pr-4 text-gray-700">
                        {isUnit ? (rec?.unit?.name || "—") : <span className="text-gray-300">—</span>}
                      </td>
                      {/* Price per unit - unit_based only */}
                      <td className="py-2 pr-4 text-right text-gray-700">
                        {isUnit ? fmtNum(rec?.price_per_unit) : <span className="text-gray-300">—</span>}
                      </td>
                      {/* Subtotal */}
                      <td className="py-2 pr-4 font-semibold text-gray-900 text-right whitespace-nowrap">
                        {fmtNum(rec?.subtotal)}
                      </td>
                      {/* Extra charge */}
                      <td className="py-2 pr-4 text-right text-gray-400">—</td>
                      {/* Discount */}
                      <td className="py-2 pr-4 text-right text-gray-400">—</td>
                      {/* Total (same as subtotal until extra/discount implemented) */}
                      <td className="py-2 pr-4 font-semibold text-gray-900 text-right whitespace-nowrap">
                        {fmtNum(rec?.subtotal)}
                      </td>
                      {/* CAT log */}
                      {/* <td className="py-2 pr-4 text-gray-400">-</td> */}
                      {/* Filename */}
                      <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">
                        {rec?.file?.file_name || "—"}
                      </td>
                      {/* Note for client */}
                      <td className="py-2 pr-4 text-gray-700">{rec?.note_for_client || "—"}</td>
                      {/* Internal note */}
                      <td className="py-2 pr-4 text-gray-700">{rec?.internal_note || "—"}</td>
                      {/* Remove */}
                      <td className="py-2 whitespace-nowrap">
                        <button
                          onClick={() => toast.error("Remove not yet implemented")}
                          className={btn("warning", "sm")}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Linked payments */}
      <Section
        title="Linked Payments"
        actionBtn={
          <ActionBtn
            onClick={() => toast.success("New linked payment not yet implemented")}
            label="New linked payment"
            color="bg-brand-600 hover:bg-brand-700"
          />
        }
      >
        <SectionTable
          headers={[
            "Code", "Linked invoices", "Coverage by invoices",
            "Payment method type", "Paid at", "Amount",
            "Linked amount", "Unlinked amount",
            "Amount covered in this invoice", "Note",
          ]}
        >
          <tr>
            <td colSpan={10} className="py-3 text-xs text-gray-400">
              No results found.
            </td>
          </tr>
        </SectionTable>
      </Section>

      {/* Related files */}
      <Section
        title="Related Files"
        actionBtn={
          <ActionBtn
            onClick={() => toast.error("Upload not yet implemented")}
            label="Upload"
            color="bg-brand-600 hover:bg-brand-700"
          />
        }
      >
        <SectionTable
          headers={["File #", "Filename", "Size", "Uploaded at", "Uploaded by"]}
        >
          <tr>
            <td colSpan={5} className="py-3 text-xs text-gray-400">
              No results found.
            </td>
          </tr>
        </SectionTable>
      </Section>

      {showDeleteModal && (
        <ConfirmModal
          title="Confirm Delete"
          message={`Are you sure you want to delete invoice ${data.invoice_code}?`}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          confirmText="Yes, Delete"
        />
      )}

      {showPdf && (
        <PdfPreviewModal
          title={`Invoice ${data.invoice_code || id}`}
          url={`/project-finances/client-invoices/${id}/pdf`}
          fileName={`Invoice ${data.invoice_code || id}.pdf`}
          onClose={() => setShowPdf(false)}
        />
      )}
    </div>
  );
};

export default ClientInvoiceDetailPage;
